import { useState, useMemo, type JSX } from 'react'
import { Box, VStack, HStack, Text, Input, Textarea, Button, Separator } from '@chakra-ui/react'
import { LuPencil } from 'react-icons/lu'
import type { Order } from '@/common/order.common'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import type { CapacityInfo } from './CheckCapacityPopup'
// import { co } from 'node_modules/@fullcalendar/core/internal-common'
import { correctOrder } from '@/api/orders.api'
// import type { ServiceType } from '@/common/order.common'
import { hoursToUnits, unitsToHours } from '@/helper/order.helper'
type UiServiceType = 'TOJ' | 'CONSULTING' | 'TRAINING' | 'PILOT'

export interface CorrectionSectionProps {
  order: Order
  quantities: Record<UiServiceType, number>
  remainingCurrentYearUnits: CapacityInfo
  agreedCapacityUnits: CapacityInfo
  onClose: () => void
  onSuccess: () => void
}

export default function CorrectionSection({
  order,
  quantities,
  remainingCurrentYearUnits,
  agreedCapacityUnits,
  onClose,
  onSuccess,
}: CorrectionSectionProps) {
  const [corrected, setCorrected] = useState<Record<UiServiceType, number>>({
    TOJ: hoursToUnits('TOJ', quantities.TOJ),
    CONSULTING: hoursToUnits('CONSULTING', quantities.CONSULTING),
    TRAINING: hoursToUnits('TRAINING', quantities.TRAINING),
    PILOT: hoursToUnits('PILOT', quantities.PILOT ?? 0),
  })

  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  /* -------------------------------------------------------------
   * REAL REMAINING CAPACITY
   * remaining = capacity - workload - corrected
   * ------------------------------------------------------------- */
  const remaining = useMemo(() => {
    return {
      TOJ: remainingCurrentYearUnits.TOJ - corrected.TOJ,
      CONSULTING: remainingCurrentYearUnits.CONSULTING - corrected.CONSULTING,
      TRAINING: remainingCurrentYearUnits.TRAINING - corrected.TRAINING,
      PILOT: (remainingCurrentYearUnits.PILOT ?? 0) - corrected.PILOT,
    }
  }, [remainingCurrentYearUnits, corrected])

  const isDisabled = reason.trim().length === 0

  async function submitCorrection() {
    if (isSubmitting) return
    const buckets = (Object.entries(corrected) as Array<[UiServiceType, number]>).map(
      ([serviceType, units]) => ({
        serviceType,
        plannedHours: unitsToHours(serviceType, units),
      })
    )
    try {
      setIsSubmitting(true)
      await correctOrder(order.id, {
        buckets,
        note: reason,
      })

      toast.success('Korrektur gesendet', 'Der SIB wurde eine Korrekturanfrage übermittelt.')

      onSuccess()
    } catch (err: any) {
      toast.error('Fehler', err?.message || 'Fehler beim Senden der Korrektur')
    } finally {
      setIsSubmitting(false)
    }
  }

  function updateValue(type: UiServiceType, value: string) {
    const clean = value.replace(',', '.')
    const num = parseFloat(clean)

    if (!isNaN(num) && num >= 0) {
      setCorrected(prev => ({ ...prev, [type]: num }))
    } else if (value === '') {
      setCorrected(prev => ({ ...prev, [type]: 0 }))
    }
  }

  return (
    <Box
      data-testid="order-correction-section"
      borderWidth="1px"
      borderColor="border.default"
      rounded="l3"
      bg="bg.subtle"
      p={4}
      maxW="900px"
    >
      <Text fontSize="sm" fontWeight="normal">
        Ihr Korrekturvorschlag
      </Text>
      <Text fontSize="2xs" fontWeight="normal" color="fg.muted" mt={1}>
        Die im Korrekturvorschlag eingetragenen Leistungen werden vom SIB bearbeitet, alle darüber
        hinaus bestellten Stunden müssen Sie anderen SIB auftragen. Der SIB erhält für alle
        ürsprünglich bestellten Leistungen die Vermittlunspauschale.
      </Text>

      {/* <Separator my={4} /> */}

      {/* Correction rows */}
      <VStack align="stretch" gap={4} mt={4}>
        {(['TOJ', 'CONSULTING', 'TRAINING'] as UiServiceType[])
          .concat((quantities.PILOT ?? 0) > 0 ? ['PILOT' as UiServiceType] : [])
          .map(type => (
            <CorrectionBlock
              key={type}
              type={type}
              ordered={hoursToUnits(type, quantities[type] ?? 0)}
              corrected={corrected[type]}
              onChange={v => updateValue(type, v)}
            />
          ))}
      </VStack>

      <Separator my={4} />

      {/* Remaining panel */}
      <Box rounded="md" bg="bg.subtle" gap={1.5}>
        <Text fontSize="sm" fontWeight="normal" color="fg.muted" mb={3}>
          Übriges Leistungskontingent nach Korrektur
        </Text>

        <HStack gap={10}>
          <QuotaBlock label="TOJ" value={remaining.TOJ} capacity={agreedCapacityUnits.TOJ} />
          <QuotaBlock
            label="Beratung"
            value={remaining.CONSULTING}
            capacity={agreedCapacityUnits.CONSULTING}
          />
          <QuotaBlock
            label="Schulung"
            value={remaining.TRAINING}
            capacity={agreedCapacityUnits.TRAINING}
          />
          {(quantities.PILOT ?? 0) > 0 && (
            <QuotaBlock
              label="Pilot"
              value={remaining.PILOT}
              capacity={agreedCapacityUnits.PILOT ?? 0}
            />
          )}
        </HStack>
        <Text fontSize="2xs" fontWeight="normal" color="fg.subtle" mt={2}>
          Überschreitungen des offenen Kontingents von {'>'}10% sind durch rote Warnungen
          gekennzeichnet.
        </Text>
        {/* {hasExceeded && (
          <Box
            mt={4}
            p={3}
            bg="yellow.subtle"
            borderWidth="1px"
            borderColor="yellow.muted"
            rounded="md"
          >
            <HStack align="start" gap={3}>
              <Box as={LuInfo} color="yellow.fg" mt="2px" />
              <VStack align="start" gap={1}>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color="yellow.fg"
                >
                  Achtung — Kontingent überschritten
                </Text>
                <Text fontSize="xs" color="yellow.fg">
                  Die korrigierten Stunden überschreiten das verfügbare
                  Leistungskontingent. Bitte prüfen Sie die Bestellung,
                  bevor Sie die Korrektur senden.
                </Text>
              </VStack>
            </HStack>
          </Box>
        )} */}
      </Box>

      <Separator my={4} />

      {/* Reason */}
      <Box>
        <Text fontSize="sm" fontWeight="semibold" mb={2}>
          Begründung *
        </Text>
        <Textarea
          border={0}
          placeholder="Begründung eintragen"
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
        <Text fontSize="xs" color="fg.muted" mt={1}>
          Bei Korrektur der Bestellung muss eine Begründung vorliegen.
        </Text>
      </Box>

      <HStack justify="flex-end" gap={4} mt={6}>
        <Button size="md" variant="plain" onClick={onClose} disabled={isSubmitting}>
          Abbrechen
        </Button>

        <Button
          variant="solid"
          size="md"
          colorPalette="blue"
          disabled={isDisabled}
          loading={isSubmitting}
          onClick={submitCorrection}
        >
          <LuPencil />
          Korrektur abschicken
        </Button>
      </HStack>
    </Box>
  )
}

/* ============================================================
 * BLOCKS
 * ============================================================ */

function CorrectionBlock({
  type,
  ordered,
  corrected,
  onChange,
}: {
  type: UiServiceType
  ordered: number
  corrected: number
  onChange: (val: string) => void
}) {
  return (
    <HStack align="flex-start" justify="space-between" gap={8} w="full" wrap="wrap">
      {/* LEFT SIDE (Ordered → Corrected) */}
      <VStack align="stretch" flex="1" minW="260px" p={2}>
        <Text fontSize="sm" fontWeight="normal" color="fg.muted">
          {mapLabel(type)}
        </Text>

        <HStack gap={10} mt={2}>
          {/* Ordered */}
          <VStack align="start" minW="100px">
            <Text fontSize="3xl" color="fg.muted" fontWeight="semibold">
              {ordered}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              Bestellt
            </Text>
          </VStack>

          {/* big arrow */}
          <Box fontSize="3xl" color="fg.muted" mt={6}>
            →
          </Box>

          {/* Corrected */}
          <VStack align="start" minW="80px">
            <Text fontSize="3xl" color="fg.muted" fontWeight="semibold">
              {corrected}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              Korrigiert
            </Text>
          </VStack>
        </HStack>
        <Input
          type="number"
          size="sm"
          value={corrected}
          onChange={e => onChange(e.target.value)}
          min={0}
        />
      </VStack>

      {/* RIGHT SIDE (Vermittlungspauschale info card) */}
      <Box flex="1" bg="bg.muted" p={4} rounded="md" borderWidth="1px" borderColor="border.subtle">
        <Text fontSize="sm" fontWeight="normal" color="fg.muted">
          {mapLabel(type)}
        </Text>

        <Text fontSize="3xl" fontWeight="semibold" color="fg.muted" mt={1}>
          {ordered}
        </Text>

        <Text fontSize="xs" color="fg.muted" fontWeight="normal" mt={2}>
          Vermittlungspauschale
        </Text>

        <Text fontSize="2xs" color="fg.muted" mt={1} lineHeight="1.4">
          Der SIB erhält für alle ürsprünglich bestellten Leistungen die Vermittlunspauschale.
        </Text>
      </Box>
    </HStack>
  )
}

function QuotaBlock({
  label,
  value,
  capacity,
}: {
  label: string
  value: number
  capacity: number
}) {
  // Compute percentage overflow relative to capacity
  const overflowPercent = value < 0 && capacity > 0 ? (Math.abs(value) / capacity) * 100 : 0

  let badge: JSX.Element | null = null

  if (value < 0) {
    if (overflowPercent <= 10) {
      // green badge
      badge = (
        <Box px={2} py={0.5} bg="green.subtle" color="green.fg" fontSize="xs" rounded="md">
          {overflowPercent.toFixed(1)}%
        </Box>
      )
    } else {
      // red badge
      badge = (
        <Box px={2} py={0.5} bg="red.subtle" color="red.fg" fontSize="xs" rounded="md">
          {overflowPercent.toFixed(1)}%
        </Box>
      )
    }
  }

  return (
    <VStack align="start" minW="80px">
      <Text fontSize="sm" color="fg.muted">
        {label}
      </Text>

      <HStack align="center" gap={2}>
        <Text fontSize="xl" fontWeight="semibold" color="fg.muted">
          {value}
        </Text>
        {badge}
      </HStack>
    </VStack>
  )
}

function mapLabel(type: UiServiceType) {
  switch (type) {
    case 'TOJ':
      return 'TOJ'
    case 'CONSULTING':
      return 'Beratung'
    case 'TRAINING':
      return 'Schulung'
    case 'PILOT':
      return 'Pilot'
  }
}
